import { loadStripe, Stripe } from '@stripe/stripe-js';
import { api } from './api';

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
    stripePromise = pk ? loadStripe(pk) : Promise.resolve(null);
  }
  return stripePromise;
}

export interface CreatePaymentIntentBody {
  planId: 'try' | 'starter' | 'pro' | 'team';
  /**
   * Three-letter ISO currency code. Omit to let the backend pick the default
   * for the plan (USD today). The backend looks up the amount for that
   * currency and 400s if the plan doesn't support it.
   */
  currency?: 'usd' | 'inr' | 'sgd';
}

export interface PaymentIntentResponse {
  clientSecret: string;
  amount: number;
  currency: string;
}

/**
 * The backend creates a PaymentIntent with automatic_payment_methods enabled,
 * which unlocks card, Apple Pay, Google Pay, WeChat Pay and Link in the
 * Payment Element — no client-side changes needed per method.
 */
export async function createPaymentIntent(body: CreatePaymentIntentBody): Promise<PaymentIntentResponse> {
  const USE_MOCKS = import.meta.env.VITE_USE_MOCKS !== 'false';
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 500));
    // Mock amounts by (plan, currency). Only used in demo mode — real amounts
    // come from the backend's PRICES table. Keep these roughly in sync with
    // lib/pricing.ts so the demo checkout summary isn't wildly off.
    const mockAmounts: Record<string, Record<string, number>> = {
      usd: { try: 300,   starter: 900,   pro: 1900,   team: 4900   },
      inr: { try: 24900, starter: 74900, pro: 159900, team: 399900 },
      sgd: { try: 400,   starter: 1200,  pro: 2500,   team: 6500   },
    };
    const curr = body.currency ?? 'usd';
    return {
      clientSecret: 'pi_mock_secret_' + Math.random().toString(36).slice(2),
      amount: mockAmounts[curr]?.[body.planId] ?? 900,
      currency: curr,
    };
  }
  const { data } = await api.post<PaymentIntentResponse>('/payments/intent', body);
  return data;
}

export interface RedeemPromoCodeBody {
  planId: 'try' | 'starter' | 'pro' | 'team';
  /** Customer-facing promo code as typed by the user. Backend normalises to uppercase. */
  promotionCode: string;
}

export interface RedeemPromoCodeResponse {
  ok: true;
  credits: number;
  planId: string;
  code: string;
}

/**
 * Redeem a 100%-off promo code without Stripe.
 *
 * Why this exists: Stripe's PaymentIntent minimum charge is ~$0.50, so a
 * fully-discounted ($0) intent is impossible. The backend bypasses Stripe for
 * 100%-off codes, validates the code, and credits the user directly via the
 * Admin SDK. Returns a synchronous success/error — no clientSecret, no
 * confirmPayment call needed on the frontend.
 *
 * The backend enforces per-code total redemption cap and per-user dedupe
 * atomically (Firestore transaction). Any error here means the redemption
 * did NOT happen — safe to retry with a different code.
 */
export async function redeemPromoCode(body: RedeemPromoCodeBody): Promise<RedeemPromoCodeResponse> {
  const { data } = await api.post<RedeemPromoCodeResponse>('/payments/redeem', body);
  return data;
}
